import { useEffect, useState, useMemo } from "react";
import {
  Select, Table, Tabs, Card, Spin, Empty, Tag, Row, Col,
  Typography, Badge, Statistic, Button, Input, Segmented, Divider
} from "antd";
import {
  CalendarOutlined, BookOutlined, TeamOutlined, ClockCircleOutlined,
  SearchOutlined, EnvironmentOutlined, FilterOutlined, ReloadOutlined,
  TableOutlined, UnorderedListOutlined, UserOutlined, BankOutlined
} from "@ant-design/icons";
import { appCRUD } from "../../actions/app";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
const DAY_COLORS = {
  monday: "#1890ff", tuesday: "#52c41a", wednesday: "#722ed1",
  thursday: "#fa8c16", friday: "#13c2c2",
};

const toTitleCase = (str) =>
  str ? str.replace(/\b\w/g, (c) => c.toUpperCase()) : "";

const PublicTimetable = () => {
  const [loading, setLoading] = useState(false);
  const [timetableData, setTimetableData] = useState([]);
  const [examData, setExamData] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [instructors, setInstructors] = useState([]);

  // Filters
  const [selectedCampus, setSelectedCampus] = useState(null);
  const [selectedAcademic, setSelectedAcademic] = useState(null);
  const [selectedSemester, setSelectedSemester] = useState(null);
  const [selectedProgramme, setSelectedProgramme] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [activeTab, setActiveTab] = useState("timetable");
  const [viewMode, setViewMode] = useState("grid");
  const [searchText, setSearchText] = useState("");

  const [timetableInfo, setTimetableInfo] = useState({});
  const [examInfo, setExamInfo] = useState({});

  // Load dropdowns on mount
  useEffect(() => {
    appCRUD.loadPublicCampuses().then((r) => setCampuses(r.data)).catch(() => {});
    appCRUD.loadPublicProgrammes().then((r) => setProgrammes(r.data)).catch(() => {});
    appCRUD.loadPublicAcademicYears().then((r) => setAcademicYears(r.data)).catch(() => {});
  }, []);

  // Load semesters when academic year changes
  useEffect(() => {
    if (selectedAcademic) {
      appCRUD.loadPublicSemesters(selectedAcademic).then((r) => setSemesters(r.data)).catch(() => setSemesters([]));
    } else {
      setSemesters([]);
    }
  }, [selectedAcademic]);

  // Load instructors when campus/academic changes
  useEffect(() => {
    appCRUD.loadPublicInstructors(selectedCampus, selectedAcademic).then((r) => setInstructors(r.data)).catch(() => setInstructors([]));
  }, [selectedCampus, selectedAcademic]);

  // Load timetable data
  useEffect(() => {
    if (activeTab === "timetable") {
      setLoading(true);
      appCRUD
        .loadPublicTimetable({
          campus: selectedCampus, academic: selectedAcademic,
          semester: selectedSemester, programme: selectedProgramme,
          year: selectedYear, day: selectedDay, instructor: selectedInstructor,
        })
        .then((resp) => {
          setTimetableData(resp.data.data || []);
          setTimetableInfo({
            campus: resp.data.campus, academic_year: resp.data.academic_year,
            semester: resp.data.semester,
          });
        })
        .catch(() => setTimetableData([]))
        .finally(() => setLoading(false));
    }
  }, [selectedCampus, selectedAcademic, selectedSemester, selectedProgramme,
      selectedYear, selectedDay, selectedInstructor, activeTab]);

  // Load exam data
  useEffect(() => {
    if (activeTab === "exam") {
      setLoading(true);
      appCRUD
        .loadPublicExam({
          campus: selectedCampus, academic: selectedAcademic,
          semester: selectedSemester, programme: selectedProgramme,
          year: selectedYear,
        })
        .then((resp) => {
          setExamData(resp.data.data || []);
          setExamInfo({
            campus: resp.data.campus, academic_year: resp.data.academic_year,
            semester: resp.data.semester, exam_period: resp.data.exam_period,
          });
        })
        .catch(() => setExamData([]))
        .finally(() => setLoading(false));
    }
  }, [selectedCampus, selectedAcademic, selectedSemester, selectedProgramme, selectedYear, activeTab]);

  // Filter by search locally
  const filtered = useMemo(() => {
    if (!searchText) return timetableData;
    const q = searchText.toLowerCase();
    return timetableData.filter(
      (s) =>
        s.course?.toLowerCase().includes(q) ||
        s.programme_code?.toLowerCase().includes(q) ||
        s.instractor_name?.toLowerCase().includes(q) ||
        s.room_code?.toLowerCase().includes(q)
    );
  }, [timetableData, searchText]);

  // Group by day
  const groupedByDay = useMemo(() => {
    const g = {};
    DAY_ORDER.forEach((d) => (g[d.toLowerCase()] = []));
    filtered.forEach((s) => {
      const day = (s.day || "").toLowerCase();
      if (g[day]) g[day].push(s);
    });
    Object.values(g).forEach((arr) =>
      arr.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""))
    );
    return g;
  }, [filtered]);

  const stats = useMemo(() => ({
    total: filtered.length,
    programmes: [...new Set(filtered.map((s) => s.programme_code))].length,
    rooms: [...new Set(filtered.map((s) => s.room_code))].length,
    instructors: [...new Set(filtered.map((s) => s.instractor).filter(i => i && !['0','-1','-2',''].includes(String(i))))].length,
  }), [filtered]);

  const clearFilters = () => {
    setSelectedCampus(null);
    setSelectedAcademic(null);
    setSelectedSemester(null);
    setSelectedProgramme(null);
    setSelectedYear(null);
    setSelectedDay(null);
    setSelectedInstructor(null);
    setSearchText("");
  };

  const formatTime = (t) => (t ? t.substring(0, 5) : "");

  const isTba = (r) => !r.instractor_name || !r.instractor_name.trim() || ['0', '-1', '-2', ''].includes(String(r.instractor));

  // Table columns
  const columns = [
    {
      title: "Day", dataIndex: "day", key: "day", width: 100,
      render: (d) => <Tag color={DAY_COLORS[(d || "").toLowerCase()]}>{toTitleCase(d)}</Tag>,
      sorter: (a, b) => DAY_ORDER.indexOf(toTitleCase(a.day)) - DAY_ORDER.indexOf(toTitleCase(b.day)),
    },
    {
      title: "Time", key: "time", width: 120,
      render: (_, r) => `${formatTime(r.start_time)} - ${formatTime(r.end_time)}`,
      sorter: (a, b) => (a.start_time || "").localeCompare(b.start_time || ""),
    },
    { title: "Course", dataIndex: "course", key: "course", width: 100, render: (t) => <strong>{t}</strong> },
    { title: "Room", dataIndex: "room_code", key: "room", width: 100, render: (t) => <Tag>{t}</Tag> },
    {
      title: "Instructor", key: "inst", width: 200,
      render: (_, r) => isTba(r) ? <Text type="secondary">TBA</Text> : toTitleCase(r.instractor_name),
    },
    { title: "Programme", dataIndex: "programme_code", key: "prog", width: 100, render: (t) => <Tag color="blue">{t}</Tag> },
    {
      title: "Year", dataIndex: "study", key: "year", width: 60, align: "center",
      render: (y) => <Badge count={y} style={{ backgroundColor: ["", "#52c41a", "#1890ff", "#722ed1", "#fa8c16"][y] || "#666" }} />,
    },
  ];

  // Session card
  const SessionCard = ({ session }) => (
    <div style={{
      background: "#fff", borderRadius: 8, padding: "10px 14px", marginBottom: 8,
      borderLeft: `4px solid ${DAY_COLORS[(session.day || "").toLowerCase()] || "#ccc"}`,
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Text strong style={{ fontSize: 15 }}>{session.course}</Text>
        <Tag color="blue" style={{ margin: 0 }}>{session.programme_code} Y{session.study}</Tag>
      </div>
      <div style={{ marginTop: 6, color: "#666", fontSize: 13 }}>
        <ClockCircleOutlined style={{ marginRight: 4 }} />
        {formatTime(session.start_time)} - {formatTime(session.end_time)}
        <span style={{ margin: "0 8px" }}>|</span>
        <EnvironmentOutlined style={{ marginRight: 4 }} />
        {session.room_code}
      </div>
      <div style={{ marginTop: 4, color: "#888", fontSize: 12 }}>
        <UserOutlined style={{ marginRight: 4 }} />
        {isTba(session) ? "Instructor: TBA" : toTitleCase(session.instractor_name)}
      </div>
    </div>
  );

  // Grid view
  const GridView = () => (
    <Row gutter={[16, 16]}>
      {DAY_ORDER.map((day) => {
        const sessions = groupedByDay[day.toLowerCase()] || [];
        return (
          <Col key={day} xs={24} sm={24} md={12} lg={Math.floor(24 / DAY_ORDER.length)}>
            <Card size="small"
              title={
                <span style={{ color: DAY_COLORS[day.toLowerCase()] }}>
                  <CalendarOutlined style={{ marginRight: 6 }} />{day}
                  <Badge count={sessions.length} style={{ backgroundColor: DAY_COLORS[day.toLowerCase()], marginLeft: 8 }} />
                </span>
              }
              style={{ minHeight: 300 }}
              bodyStyle={{ padding: "8px 12px", maxHeight: 600, overflowY: "auto" }}>
              {sessions.length === 0 ? (
                <Empty description="No sessions" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                sessions.map((s, i) => <SessionCard key={i} session={s} />)
              )}
            </Card>
          </Col>
        );
      })}
    </Row>
  );

  const info = activeTab === "timetable" ? timetableInfo : examInfo;

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)" }}>
      {/* Header */}
      <div style={{
        background: "linear-gradient(90deg, #25bb57 0%, #2596be 100%)",
        padding: "30px 0 20px", textAlign: "center", color: "#fff",
        borderBottom: "4px solid #dee300",
      }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "0 20px" }}>
          <img src="/assets/images/suza.jpeg" alt="SUZA" width={80}
            style={{ borderRadius: "50%", marginBottom: 10, border: "3px solid rgba(255,255,255,0.3)" }} />
          <Title level={2} style={{ color: "#fff", margin: "0 0 4px" }}>
            RATIBA - SUZA Timetable
          </Title>
          <Paragraph style={{ color: "rgba(255,255,255,0.85)", margin: 0, fontSize: 16 }}>
            The State University of Zanzibar - Teaching &amp; Examination Schedule
          </Paragraph>
          {info.campus && (
            <div style={{ marginTop: 12, display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
              <Tag color="green" style={{ fontSize: 14, padding: "4px 12px" }}>
                <BankOutlined style={{ marginRight: 4 }} />{toTitleCase(info.campus)}
              </Tag>
              <Tag color="blue" style={{ fontSize: 14, padding: "4px 12px" }}>
                <CalendarOutlined style={{ marginRight: 4 }} />{info.academic_year}
              </Tag>
              <Tag color="purple" style={{ fontSize: 14, padding: "4px 12px" }}>
                <BookOutlined style={{ marginRight: 4 }} />{toTitleCase(info.semester)}
              </Tag>
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "20px" }}>
        {/* Filters Row 1: Campus, Academic Year, Semester */}
        <Card style={{ marginBottom: 12 }} size="small"
          title={<span><FilterOutlined style={{ marginRight: 6 }} />Select Timetable</span>}>
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={8}>
              <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Campus</label>
              <Select placeholder="Select Campus" style={{ width: "100%" }} allowClear
                onChange={(v) => { setSelectedCampus(v); }} value={selectedCampus}
                suffixIcon={<EnvironmentOutlined />}>
                {campuses.map((c) => (
                  <Option key={c.compas_id} value={c.compas_id}>{toTitleCase(c.name)}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={8}>
              <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Academic Year</label>
              <Select placeholder="Academic Year" style={{ width: "100%" }} allowClear
                onChange={(v) => { setSelectedAcademic(v); setSelectedSemester(null); }} value={selectedAcademic}
                suffixIcon={<CalendarOutlined />}>
                {academicYears.map((y) => (
                  <Option key={y.academic_id} value={y.academic_id}>
                    {y.name} {y.is_active && <Tag color="green" style={{ marginLeft: 4 }}>Active</Tag>}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={8}>
              <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Semester</label>
              <Select placeholder={selectedAcademic ? "Select Semester" : "Select Academic Year first"} style={{ width: "100%" }} allowClear
                disabled={!selectedAcademic}
                onChange={setSelectedSemester} value={selectedSemester}>
                {semesters.map((s) => (
                  <Option key={s.semister_id} value={s.semister_id}>{toTitleCase(s.name)}</Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Filters Row 2: Programme, Year, Day, Instructor, Search */}
        <Card style={{ marginBottom: 16 }} size="small"
          title={<span><SearchOutlined style={{ marginRight: 6 }} />Filter &amp; Search</span>}>
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={8} md={5}>
              <Select placeholder="Programme" style={{ width: "100%" }} allowClear showSearch optionFilterProp="children"
                onChange={setSelectedProgramme} value={selectedProgramme} suffixIcon={<BookOutlined />}>
                {programmes.map((p) => (
                  <Option key={`${p.id}-${p.code}`} value={p.code}>{p.code} - {toTitleCase(p.name)}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} sm={4} md={3}>
              <Select placeholder="Year" style={{ width: "100%" }} allowClear onChange={setSelectedYear} value={selectedYear}>
                {[1, 2, 3, 4, 5].map((y) => <Option key={y} value={y}>Year {y}</Option>)}
              </Select>
            </Col>
            {activeTab === "timetable" && (
              <Col xs={12} sm={4} md={3}>
                <Select placeholder="Day" style={{ width: "100%" }} allowClear onChange={setSelectedDay} value={selectedDay}>
                  {DAY_ORDER.map((d) => <Option key={d} value={d.toLowerCase()}>{d}</Option>)}
                </Select>
              </Col>
            )}
            <Col xs={24} sm={8} md={5}>
              <Select placeholder="Instructor" style={{ width: "100%" }} allowClear showSearch optionFilterProp="children"
                onChange={setSelectedInstructor} value={selectedInstructor} suffixIcon={<UserOutlined />}>
                {instructors.map((i) => (
                  <Option key={i.id} value={i.id}>{toTitleCase(i.name)}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={18} sm={6} md={5}>
              <Input placeholder="Search course, room..." prefix={<SearchOutlined />} allowClear
                value={searchText} onChange={(e) => setSearchText(e.target.value)} />
            </Col>
            <Col xs={6} sm={4} md={3}>
              <Button icon={<ReloadOutlined />} onClick={clearFilters} block>Clear All</Button>
            </Col>
          </Row>
        </Card>

        {/* Stats */}
        {filtered.length > 0 && activeTab === "timetable" && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={6}>
              <Card size="small">
                <Statistic title="Sessions" value={stats.total} prefix={<ClockCircleOutlined />} valueStyle={{ color: "#1890ff" }} />
              </Card>
            </Col>
            <Col xs={6}>
              <Card size="small">
                <Statistic title="Programmes" value={stats.programmes} prefix={<BookOutlined />} valueStyle={{ color: "#52c41a" }} />
              </Card>
            </Col>
            <Col xs={6}>
              <Card size="small">
                <Statistic title="Rooms" value={stats.rooms} prefix={<EnvironmentOutlined />} valueStyle={{ color: "#722ed1" }} />
              </Card>
            </Col>
            <Col xs={6}>
              <Card size="small">
                <Statistic title="Instructors" value={stats.instructors} prefix={<UserOutlined />} valueStyle={{ color: "#fa8c16" }} />
              </Card>
            </Col>
          </Row>
        )}

        {/* Content */}
        <Card>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            tabBarExtraContent={
              activeTab === "timetable" ? (
                <Segmented value={viewMode} onChange={setViewMode} options={[
                  { value: "grid", icon: <TableOutlined /> },
                  { value: "list", icon: <UnorderedListOutlined /> },
                ]} />
              ) : null
            }
            items={[
              {
                key: "timetable",
                label: <span><CalendarOutlined style={{ marginRight: 4 }} />Teaching Timetable</span>,
                children: (
                  <Spin spinning={loading}>
                    {filtered.length === 0 ? (
                      <Empty description="No timetable data. Select a campus, academic year, and semester to begin." />
                    ) : viewMode === "grid" ? (
                      <GridView />
                    ) : (
                      <Table dataSource={filtered} columns={columns} rowKey="id"
                        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} sessions` }}
                        size="small" bordered scroll={{ x: 900 }} />
                    )}
                  </Spin>
                ),
              },
              {
                key: "exam",
                label: <span><FilterOutlined style={{ marginRight: 4 }} />Examination Timetable</span>,
                children: (
                  <Spin spinning={loading}>
                    {examData.length === 0 ? (
                      <Empty description="No exam timetable data found." />
                    ) : (
                      <Table dataSource={examData} columns={[
                        { title: "Date", dataIndex: "exam_date", key: "date", render: (t) => <Tag color="red">{t}</Tag> },
                        { title: "Time", key: "time", render: (_, r) => `${formatTime(r.start_time)} - ${formatTime(r.end_time)}` },
                        { title: "Course", dataIndex: "course", key: "course", render: (t) => <strong>{t}</strong> },
                        { title: "Course Name", dataIndex: "course_name", key: "name" },
                        { title: "Programme", dataIndex: "programme_code", key: "prog", render: (t) => <Tag color="blue">{t}</Tag> },
                        { title: "Year", dataIndex: "study_year", key: "year" },
                      ]} rowKey="id" pagination={{ pageSize: 20 }} size="small" bordered />
                    )}
                  </Spin>
                ),
              },
            ]}
          />
        </Card>

        <div style={{ textAlign: "center", marginTop: 30, padding: 20 }}>
          <Text type="secondary">&copy; {new Date().getFullYear()} The State University of Zanzibar (SUZA)</Text>
          <br />
          <Text type="secondary" style={{ fontStyle: "italic", fontSize: 12 }}>
            ---- catalyst for social change ----
          </Text>
        </div>
      </div>
    </div>
  );
};

export default PublicTimetable;
